/**
 * Cards first, table second: an agent's queue opens as cards (the API's
 * built-in queue board, `queue-<key>`) unless they picked the table, which
 * is remembered per browser. Filtered views (product, chips, search) stay on
 * the table, which is what those filters belong to.
 */

export const QUEUE_KEYS = ['all', 'mine', 'sla_risk', 'gxp', 'escalated'] as const
export type QueueKey = (typeof QUEUE_KEYS)[number]
export type QueueView = 'cards' | 'table'

const PREF = 'ss.queue.view'

export const queueBoardId = (queue: QueueKey) => `queue-${queue}`

export function queueOfBoardId(id: string): QueueKey | null {
  const q = id.startsWith('queue-') ? id.slice('queue-'.length) : ''
  return (QUEUE_KEYS as readonly string[]).includes(q) ? (q as QueueKey) : null
}

/** The table URL of a queue (with view=table so it does not bounce back to cards). */
export function queueTableUrl(queue: QueueKey): string {
  return queue === 'all' ? '/queue?view=table' : `/queue?queue=${queue}&view=table`
}

/** Only a plain queue has cards: any other filter in the URL means the table. */
export function hasOnlyQueue(params: URLSearchParams): boolean {
  return [...params.keys()].every((k) => k === 'queue')
}

export function preferredQueueView(storage: Pick<Storage, 'getItem'> = localStorage): QueueView {
  try {
    return storage.getItem(PREF) === 'table' ? 'table' : 'cards'
  } catch {
    return 'cards'
  }
}

export function rememberQueueView(view: QueueView, storage: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    storage.setItem(PREF, view)
  } catch {
    // Private mode: the choice just isn't remembered.
  }
}
